import { Input } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetChannelVideoLightList } from 'services/channel';
import VideoCard from './VideoCard';

interface IVideoSelectionStepProps {
  channelId: string;
  selectedVideoIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

type CheckboxState = 'none' | 'partial' | 'all';

const VideoSelectionStep = ({
  channelId,
  selectedVideoIds,
  onSelectionChange,
}: IVideoSelectionStepProps) => {
  const { t } = useTranslation();
  const [titleFilter, setTitleFilter] = useState('');
  const [videoIdFilter, setVideoIdFilter] = useState('');

  const getVideos = useGetChannelVideoLightList(channelId);

  const videos = useMemo(() => {
    const allVideos = getVideos.data?.slice(0, 15) ?? [];
    return allVideos;
  }, [getVideos.data]);

  const filteredVideos = useMemo(() => {
    return videos.filter(video => {
      const matchesTitle =
        !titleFilter ||
        video.videoTitle.toLowerCase().includes(titleFilter.toLowerCase());
      const matchesVideoId =
        !videoIdFilter ||
        video.externalVideoId
          .toLowerCase()
          .includes(videoIdFilter.toLowerCase());
      return matchesTitle && matchesVideoId;
    });
  }, [videos, titleFilter, videoIdFilter]);

  const selectAllState: CheckboxState = useMemo(() => {
    if (filteredVideos.length === 0) return 'none';
    const selectedCount = filteredVideos.filter(v =>
      selectedVideoIds.includes(v.id)
    ).length;
    if (selectedCount === 0) return 'none';
    if (selectedCount === filteredVideos.length) return 'all';
    return 'partial';
  }, [filteredVideos, selectedVideoIds]);

  useEffect(() => {
    const checkbox = document.querySelector(
      'input[name="selectAll"]'
    ) as HTMLInputElement;
    if (checkbox) {
      checkbox.indeterminate = selectAllState === 'partial';
    }
  }, [selectAllState]);

  const handleSelectAll = () => {
    if (selectAllState === 'all') {
      const filteredIds = filteredVideos.map(v => v.id);
      onSelectionChange(
        selectedVideoIds.filter(id => !filteredIds.includes(id))
      );
    } else {
      const allIds = [...selectedVideoIds, ...filteredVideos.map(v => v.id)];
      const uniqueIds = Array.from(new Set(allIds));
      onSelectionChange(uniqueIds);
    }
  };

  const handleVideoToggle = (videoId: string) => {
    if (selectedVideoIds.includes(videoId)) {
      onSelectionChange(selectedVideoIds.filter(id => id !== videoId));
    } else {
      onSelectionChange([...selectedVideoIds, videoId]);
    }
  };

  return (
    <div className='flex flex-col gap-4 w-full'>
      <div className='flex gap-4 justify-center'>
        <FormInput.Text
          name='titleFilter'
          type='text'
          label={t('channel.bulkAssign.filters.title')}
          placeholder={t('channel.bulkAssign.filters.titlePlaceholder')}
          value={titleFilter}
          onChange={e => setTitleFilter(e.target.value)}
        />
        <FormInput.Text
          name='videoIdFilter'
          type='text'
          label={t('channel.bulkAssign.filters.videoId')}
          placeholder={t('channel.bulkAssign.filters.videoIdPlaceholder')}
          value={videoIdFilter}
          onChange={e => setVideoIdFilter(e.target.value)}
        />
      </div>

      <div className='flex items-center gap-2 justify-between'>
        <div>
          <Input.Checkbox
            name='selectAll'
            label={t('channel.bulkAssign.selectAll')}
            checked={selectAllState === 'all'}
            onChange={handleSelectAll}
          />
        </div>
        <span className='text-sm text-gray-500'>
          ({selectedVideoIds.length} {t('channel.bulkAssign.selected')})
        </span>
      </div>

      <div className='flex flex-col gap-4 max-h-[500px] overflow-y-auto'>
        {filteredVideos.map(video => (
          <VideoCard
            key={video.id}
            video={video}
            isSelected={selectedVideoIds.includes(video.id)}
            onToggle={() => handleVideoToggle(video.id)}
          />
        ))}
      </div>

      {filteredVideos.length === 0 && (
        <div className='text-center text-gray-500 py-8'>
          {t('channel.bulkAssign.noVideos')}
        </div>
      )}
    </div>
  );
};

export default VideoSelectionStep;
